import moment from 'moment';
import { type Config as FrontRoyalConfigType } from 'FrontRoyalConfig';
import moize from 'moize';
import { addInDefaultTimeZone } from 'DateHelpers';
import { type Brand } from 'AppBranding';
import { getProgramConfigValue, type ProgramType } from 'Program';
import { type IdVerificationPeriod, type Cohort } from './Cohort.types';
import { verificationPeriodStartDate, verificationPeriodPastDue } from './IdVerificationPeriod';

function _formatNameAndStartDate({ name, startDate: startDateTimestamp }: { name: string; startDate: number }) {
    const startDate = new Date(1000 * startDateTimestamp);
    const starts = startDate! < new Date() ? 'Started' : 'Starts';
    return `${name} (${starts}: ${moment(startDate).format('MMM Do, YYYY')})`;
}

export const formatNameAndStartDate = moize(_formatNameAndStartDate);

export function getActiveIdVerificationPeriod(cohort: Cohort): IdVerificationPeriod | null {
    return (
        cohort.idVerificationPeriods
            ?.slice()
            .reverse()
            .find(period => verificationPeriodStartDate(cohort, period) < new Date()) || null
    );
}

export function getActiveIdVerificationPeriodIndex(cohort: Cohort): number | null {
    const index = cohort.idVerificationPeriods
        ?.slice()
        .reverse()
        .findIndex(period => verificationPeriodStartDate(cohort, period) < new Date());
    return index == null || index < 0 ? null : index;
}

export function activeIdVerificationPeriodIsPastDue(cohort: Cohort): boolean {
    const activeIdVerificationPeriod = getActiveIdVerificationPeriod(cohort);
    if (!activeIdVerificationPeriod) return false;
    return verificationPeriodPastDue(cohort, activeIdVerificationPeriod);
}

// Eventually we may need to support other offsetFroms here. For example, graduation_date is offset from the end
type OffsetFrom = 'startDate';

export function _getRelativeDate(cohort: Cohort, daysOffset: number, offsetFrom?: OffsetFrom): Date;
export function _getRelativeDate(cohort: Cohort, daysOffset: null | undefined, offsetFrom?: OffsetFrom): null;
export function _getRelativeDate(
    cohort: Cohort,
    daysOffset: null | undefined | number,
    offsetFrom: OffsetFrom = 'startDate',
) {
    if (daysOffset == null) return null;

    const baseDate = new Date(1000 * cohort[offsetFrom]);
    return addInDefaultTimeZone(baseDate, daysOffset, 'days');
}

export const getRelativeDate = moize(_getRelativeDate);

export function getStreamIsInCurriculum(cohort: Cohort, streamLocalePackId: string): boolean {
    if (!cohort) return false;
    return !!cohort.streamLocalePacksInfo[streamLocalePackId];
}

export function getConcentrationPlaylistPackIds(cohort: Cohort | null) {
    if (!cohort) return [];

    return cohort.playlistCollections.reduce(
        (memo: string[], playlistCollection) => memo.concat(playlistCollection.requiredPlaylistPackIds),
        [],
    );
}

export function getSpecializationPlaylistPackIds(cohort: Cohort) {
    return cohort.specializationPlaylistPackIds;
}

export function getFoundationsPlaylistPackId(cohort: Cohort) {
    if (!cohort.supportsFoundationsPlaylist) return null;

    return cohort.foundationsPlaylistLocalePack?.id || null;
}

export function getPlaylistPackIds(cohort: Cohort) {
    return getConcentrationPlaylistPackIds(cohort).concat(cohort.specializationPlaylistPackIds);
}

export function pastEnrollmentDeadline(cohort: Cohort) {
    const deadline = getRelativeDate(cohort, cohort.enrollmentDeadlineDaysOffset);
    if (!deadline) return false;
    return new Date() > deadline;
}

export function pastEndDate(cohort: Cohort) {
    const endDate = new Date(1000 * cohort.endDate);
    return endDate < new Date();
}

export const getPeriod = (cohort: Cohort, index?: number | null | undefined) => {
    index ??= cohort.currentPeriodIndex;

    if (index === null || index === undefined) return null;

    return cohort.periods[index] ?? null;
};

export const getPeriodDates = (cohort: Cohort, index: number) => {
    let rollingDate = moment(1000 * cohort.startDate);

    const dates = cohort.periods.map(period => {
        rollingDate = addInDefaultTimeZone(rollingDate, period.daysOffset, 'days');
        const startDate = rollingDate.toDate();
        rollingDate = addInDefaultTimeZone(rollingDate, period.days, 'days');
        const endDate = rollingDate.toDate();
        return { startDate, endDate };
    });

    return dates[index];
};

export function getRequiredStreamPackIdsFromPeriods(cohort: Cohort, maxPeriod?: number) {
    if (maxPeriod == null) {
        maxPeriod = cohort.periods.length - 1;
    }

    if (maxPeriod < 0) return [];

    const localePackIds = cohort.periods
        .slice(0, maxPeriod + 1)
        .flatMap(period => period.streamEntries)
        .reduce((acc: string[], streamEntry) => {
            if (streamEntry.required) return acc.concat([streamEntry.localePackId]);
            return acc;
        }, []);

    return [...new Set(localePackIds)];
}

export function getPeriodIndexForRequiredLocalePackId(cohort: Cohort, localePackId: string) {
    return cohort.periods.findIndex(period =>
        period.streamEntries.some(streamEntry => streamEntry.localePackId === localePackId && streamEntry.required),
    );
}

export function requiresTILADisclosure(cohort: Cohort) {
    // We started requiring TILA disclosures in cycle 52; however, the process
    // wasn't automated until cycle 54 (see supports_automated_tila_disclosure?).
    return (
        getProgramConfigValue(cohort.programType, 'requiresTILADisclosure') &&
        new Date(1000 * cohort.startDate) >= new Date('2023/04/24')
    );
}

// Frontend master gate for exam proctoring, mirroring Cohort#requires_proctoring? on the server.
// Currently returns false everywhere: biosigEnabled() is the (misleadingly named) proctoring flag,
// fed by the `biosig_enabled` config key which is sourced from the server's Proctoring::Config, and
// no provider is wired up since the BioSig integration was removed. The ExamVerification module is
// dormant scaffold behind this gate.
// FIXME: this is duplicated in schedulable, but we need to make a base cohort type to fix it
export function requiresProctoring(cohort: Cohort, frontRoyalConfig: FrontRoyalConfigType) {
    return (
        frontRoyalConfig.biosigEnabled() &&
        getProgramConfigValue(cohort.programType, 'requiresProctoring') &&
        // We started requiring proctoring in cycle 54.
        // Compare against UTC time because the start date of
        // Mon, 14 Aug 2023 08:00:00 UTC
        // is Sun Aug 13 2023 22:00:00 GMT-1000 in Hawaii-Aleutian Standard Time
        new Date(1000 * cohort.startDate) >= new Date('2023-08-14T08:00:00Z')
    );
}

export function getIsStreamInFoundationsPlaylist(cohort: Cohort, streamLocalePackId: string) {
    return cohort.foundationsLessonStreamLocalePackIds.includes(streamLocalePackId);
}

export function getStartDate(cohort: Cohort) {
    return new Date(1000 * cohort.startDate);
}

export function getRegistrationDeadline(cohort: Cohort) {
    return getRelativeDate(cohort, cohort.registrationDeadlineDaysOffset);
}

export function getApplicableAdmissionRound(cohort: Cohort, appliedAt?: number) {
    if (appliedAt) {
        // There are known cases of users who apply for a cohort, but ultimately end up being placed in a previous cohort (typically at the request of the user).
        // In these situations, the appliedAt value on the program application will be a more recent date than all of the application deadlines for that cohort's
        // admission rounds. This can cause getApplicableAdmissionRound to return null, which can cause various issues for users, such as not being able to edit their
        // application prior to admission. To prevent this, we should simply return the final admission round of the cohort.
        return (
            cohort.admissionRounds.find(
                admissionRound =>
                    new Date(1000 * appliedAt) < getRelativeDate(cohort, admissionRound.applicationDeadlineDaysOffset),
            ) ?? cohort.admissionRounds.at(-1)
        );
    }

    const now = new Date();
    const startDate = new Date(1000 * cohort.startDate);
    if (startDate < now) {
        return null;
    }

    return cohort.admissionRounds.find(
        admissionRound => getRelativeDate(cohort, admissionRound.applicationDeadlineDaysOffset) > now,
    );
}

export function getApplicationDeadline(cohort: Cohort) {
    const admissionRound = getApplicableAdmissionRound(cohort);
    if (!admissionRound) return null;
    return getRelativeDate(cohort, admissionRound.applicationDeadlineDaysOffset);
}

export function registrationOpenDate(cohort: Cohort) {
    return new Date(moment(getRegistrationDeadline(cohort)).subtract(180, 'days').valueOf());
}

export function afterRegistrationOpenDate(cohort: Cohort) {
    if (getProgramConfigValue(cohort.programType, 'isExecEd')) return true; // HACK: https://trello.com/c/gbvEER9G
    return moment().valueOf() >= moment(registrationOpenDate(cohort)).startOf('day').valueOf();
}

export function registrationDeadlineHasPassed(cohort: Cohort) {
    return moment().valueOf() >= moment(getRegistrationDeadline(cohort)).valueOf();
}

// NOTE: this will return false after the end date of the cohort, when currentPeriodIndex is null, see also
// isNullPeriodAfterEndDate
export const isPeriodStarted = (cohort: Cohort | null, periodIndex: number) => {
    if (!cohort) return false;
    if (cohort.currentPeriodIndex == null) return false;

    return periodIndex >= 0 && cohort.currentPeriodIndex >= periodIndex;
};

function getSupportEmailDomain(programType: ProgramType): string | null {
    return getProgramConfigValue(programType, 'supportEmailDomain');
}

function getSupportEmailUsernameForProgramType(programType: ProgramType): string | null {
    return getProgramConfigValue(programType, 'supportEmailUsername');
}

export function getSupportEmailAddress(cohort: Cohort, username?: string | null): string | null {
    if (username === cohort.programType) {
        // Some program types don't use the program type as the username for the default
        // support email address. For example, the default support email address for
        // emba_strategic_leadership isn't emba_strategic_leadership@some_domain.com;
        // it's emba@some_domain.com.
        throw new Error('Do not pass in the program_type as the supportEmailAddress username');
    }

    const programType = cohort.programType;
    const resolvedUsername = username ?? getSupportEmailUsernameForProgramType(programType);
    const domain = getSupportEmailDomain(programType);

    if (!resolvedUsername || !domain) {
        return null;
    }

    return `${resolvedUsername}@${domain}`;
}

export const isNullPeriodAfterEndDate = (cohort: Cohort | null) => {
    if (!cohort) return false;

    return cohort.currentPeriodIndex === null && new Date() > new Date(1000 * cohort.endDate);
};

export function getBranding(cohort: Cohort): Brand {
    return getProgramConfigValue<Brand>(cohort.programType, 'branding')!;
}

export function getIsExecEd(cohort: Cohort): boolean {
    return !!getProgramConfigValue<boolean>(cohort.programType, 'isExecEd');
}

export function getProgramAchievementGraphicProgramTitleLines(cohort: Cohort): string[] {
    return getProgramConfigValue<string[]>(cohort.programType, 'programAchievementGraphicProgramTitleLines') ?? [];
}
